import Collapse from '@mui/material/Collapse';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import { useFormState } from 'informed';
import { ErrorP } from '../common/Typography';
import { extractFullNamesFromErrors } from './QuoteForms/utility';
import { List, ListItem, Link } from '@mui/material';
import { QuoteWizardQuestionInstance } from '../QuoteWizard/classes';

type FormErrorsWarningProps = {
  error?: string | undefined;
  questions?: Array<QuoteWizardQuestionInstance>;
};

export const FormErrorsWarning = ({ error, questions }: FormErrorsWarningProps) => {
  const formState = useFormState();

  const getIDAndDisplayName = (fieldName: string) => {
    // get the index of the last opening bracket in the string
    // this is necessary because some nested questions will not contain their full
    // field name, since the ids in informed material are set to be the api name only
    // however, the div surrounding the parent will have the full field name as its id
    // linking to the parent element is good enough for our purposes
    const lastOpeningBracketIndex = fieldName.lastIndexOf('[');

    // gets the display name by finding the question that matches the field name
    // const displayName = questions?.find((q) => q.generateFieldName() === fieldName)?.displayName ?? "General Error";
    const questionInstance = questions?.find((q) => q.generateFieldName() === fieldName);

    // prefer to use the label, then the display name, then the api name
    let displayName = questionInstance?.label ?? questionInstance?.displayName ?? questionInstance?.apiName;
    
    // if the display name cannot be found, then we try to create a display name from the field name
    if (!displayName) {
      const lastDotIndex = fieldName.lastIndexOf('.');
      const lastDotString = fieldName.substring(lastDotIndex + 1);
      displayName = lastDotString.replace("_", " ");
      displayName = displayName.charAt(0).toUpperCase() + displayName.slice(1);
    }

    // if there are no brackets, it means that the question is not a nested question
    if (lastOpeningBracketIndex === -1) {
      return { id: fieldName, displayName };
    } 

    // otherwise, we need to match the id up to (and excluding) the first opening bracket
    // aka longest prefix matching 
    const splicedFieldName = fieldName.substring(0, lastOpeningBracketIndex);
    return { id: splicedFieldName, displayName };
  }

  return (
    <Collapse in={Object.keys(formState.errors).length > 0 || !!error}>
      <Alert severity='error' sx={{ mb: 3, mt: 3 }}>
        <Typography>
          The form above contains errors. Please review the answers carefully
          before continuing.
        </Typography>
        <List>
          {extractFullNamesFromErrors(formState.errors).map((err) => {
            const { id, displayName } = getIDAndDisplayName(err);
            return (
              <ListItem
                style={{ listStyleType: 'disc' }}
              >
                <Link
                  component="button"
                  color="error"
                  onClick={() => {
                    if (id) {
                      const element = document.getElementById(id);
                      
                      if (element) {
                        // we do this manual scrolling because the header can block the question
                        const elementPosition = element.getBoundingClientRect().top;
                        const offsetPosition = elementPosition + window.pageYOffset - 100; // 100px offset

                        window.scrollTo({
                          top: offsetPosition,
                          behavior: 'smooth',
                        });
                      }
                    }
                  }}
                  sx={{
                    cursor: 'pointer',
                    textAlign: 'left',
                    '&:hover': {
                      textDecoration: 'underline',
                    }
                  }}
                >
                  - {displayName}
                </Link>
              </ListItem>
            )
          })}
        </List>
        {error ? <ErrorP>{error}</ErrorP> : null}
      </Alert>
    </Collapse>
  );
};
